import type {ReactElement, ReactNode} from 'react';
import {useRef} from 'react';
import {PickerInputField} from '@Components/color-picker-v2/components/picker-input-field';
import {CanvasColorPicker} from '@Components/color-picker-v2/components/canvas-color-picker';
import styles from './input-section.module.scss';
import type {RGBA} from '@Utils/color.util';
import {hexToRgba, rgbaToHex} from '@Utils/color.util';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Tooltip} from '@Components/tooltip';
import {Text, TextSize} from '@Components/text';
import {useCopyToClipboardWithFeedback} from '@Hooks/useCopyToClipboardWithFeedback';
import {AlphaPercentagePickerInputField} from '@Components/color-picker-v2/components/alpha-percentage-picker-input-field/alpha-percentage-picker-input-field';
import {EyedropperColorPicker, EyeDropperCTAType, EyeDropperIconCTASize} from '@Components/eyedropper-color-picker/eyedropper-color-picker';
import useEyeDropper from 'use-eye-dropper';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';

interface InputSectionProps {
  className?: string;
  color: RGBA;
  isAlphaDisabled?: boolean;

  onChangeColor(color: RGBA | null): void;

  onChangeEnded(color: RGBA | null): void;

  onChangeColorPickerMode?(state: boolean): void;

  showPicker?: boolean;
  /** Rendered before the hex/alpha field, e.g. the popup layout's preview circle. */
  leadingContent?: ReactNode;
  /** Size of the hex and alpha fields; when omitted they keep the side panel's fixed 38px height. */
  inputFieldSize?: InputFieldSize;
  /** Shows a copy-hex button at the end of the hex/alpha field. */
  hasCopyControl?: boolean;
}

export function InputSection({
  isAlphaDisabled = false,
  className = '',
  onChangeColorPickerMode = (): void => {},
  showPicker = true,
  leadingContent = null,
  inputFieldSize,
  hasCopyControl = false,
  ...props
}: InputSectionProps): ReactElement | null {
  const containerRef = useRef<HTMLDivElement>(null);
  const {isSupported: isEyeDropperSupported} = useEyeDropper();
  const {onCopy: copyHex, doShowFeedback: isHexCopiedFeedbackShown} = useCopyToClipboardWithFeedback(rgbaToHex(props.color, false).toUpperCase());

  const hasCanvasPages = (): boolean => {
    return (window.posterEditor?.whiteboard?.pages.getPagesInOrder() ?? []).length > 0;
  };

  const getFieldHeightClassName = (): string => {
    return inputFieldSize === undefined ? styles.inputContainerHeight : '';
  };

  const isCompact = inputFieldSize === InputFieldSize.XSMALL;

  const getHexInputClassName = (): string => {
    if (isAlphaDisabled) {
      return styles.pickerInputWithoutAlpha;
    }
    return isCompact ? styles.compactPickerInput : styles.pickerInput;
  };

  const getCopyHexButton = (): ReactElement | null => {
    if (!hasCopyControl) {
      return null;
    }
    return (
      <span className={styles.copyHexButtonContainer}>
        <Icon
          icon="icon-copy"
          size={IconSize.SIZE_ICON_16}
          type={IconType.GHOST}
          shape={IconShape.SQUARE}
          onClick={copyHex}
          isUsedAsButton
          ariaLabel={window.i18next.t('pmwjs_copy')}
        />
        {getHexCopiedTooltip()}
      </span>
    );
  };

  const getHexCopiedTooltip = (): ReactElement | null => {
    if (!isHexCopiedFeedbackShown) {
      return null;
    }
    return (
      <Tooltip className={styles.hexCopiedTooltip} id="copy-hex-button-tooltip" text={<Text val={window.i18next.t('pmwjs_copied_share_dropdown_link')} size={TextSize.SM} />} />
    );
  };

  const getColorPicker = (): ReactElement | null => {
    if (!showPicker) {
      return null;
    }

    if (isEyeDropperSupported()) {
      return (
        <EyedropperColorPicker
          onColorPicked={(val: string): void => {
            const color = hexToRgba(val);
            props.onChangeEnded(color);
          }}
          ctaType={EyeDropperCTAType.ICON}
          iconCtaSize={isCompact ? EyeDropperIconCTASize.COMPACT : EyeDropperIconCTASize.DEFAULT}
        />
      );
    }

    if (hasCanvasPages()) {
      return (
        <CanvasColorPicker
          className={styles.picker}
          iconCtaSize={isCompact ? EyeDropperIconCTASize.COMPACT : EyeDropperIconCTASize.DEFAULT}
          color={props.color}
          onChangeColor={(value: RGBA | null): void => {
            props.onChangeColor(value);
          }}
          onChangeEnded={(value: RGBA | null): void => {
            props.onChangeEnded(value);
          }}
          onChangeColorPickerMode={(state): void => {
            onChangeColorPickerMode(state);
          }}
        />
      );
    }

    return null;
  };

  return (
    <div ref={containerRef} className={`${className} spacing-m-t-5 flex-h-row`}>
      {leadingContent}
      <div className={`${styles.inputContainer} radius-4 hover-transition-all`}>
        <div className={getHexInputClassName()}>
          <PickerInputField
            inputContainerClassName={getFieldHeightClassName()}
            size={inputFieldSize}
            color={props.color}
            onChangeColor={(value: RGBA | null): void => {
              props.onChangeColor(value);
            }}
            onChangeEnded={(value: RGBA | null): void => {
              props.onChangeEnded(value);
            }}
            inputClassname={styles.pickerInputField}
          />
        </div>
        {!isAlphaDisabled ? <div className={`${styles.divider} ${isCompact ? styles.compactDivider : '_full-height'}`} /> : null}
        {!isAlphaDisabled ? (
          <div className={isCompact ? styles.compactAlphaInput : styles.alphaInput}>
            <AlphaPercentagePickerInputField
              className={getFieldHeightClassName()}
              size={inputFieldSize}
              color={props.color}
              onChangeColor={(value: RGBA | null): void => {
                props.onChangeColor(value);
              }}
              onChangeEnded={(value: RGBA | null): void => {
                props.onChangeEnded(value);
              }}
              inputClassName={styles.alphaInputField}
            />
          </div>
        ) : (
          []
        )}
        {getCopyHexButton()}
      </div>
      {getColorPicker()}
    </div>
  );
}
